import { ASSETS, waLink } from "@/data/restaurant";
import { formatNaira } from "@/data/menu";
import { useOrderCapture } from "@/components/OrderCapture";

// Picture-first tiles featuring real Meritz plates with price + click-to-WA.
const SHOTS = [
  {
    src: ASSETS.barbecueFishPlantain,
    label: "Barbecue Fish + Plantain",
    tag: "Grills",
    price: 8000,
    span: "col-span-2 lg:col-span-2 lg:row-span-2",
  },
  {
    src: ASSETS.vegetableChicken,
    label: "Big Peppered Chicken",
    tag: "Proteins",
    price: 4500,
  },
  {
    src: ASSETS.bitterLeafSoup,
    label: "Bitter Leaf Soup",
    tag: "Soups",
    price: 1000,
  },
  {
    src: ASSETS.shawarmaPress,
    label: "Sharwama Special",
    tag: "Grills",
    price: 6000,
  },
  {
    src: ASSETS.abacha,
    label: "Abacha (African Salad)",
    tag: "Local",
    price: 3000,
  },
  {
    src: ASSETS.jollofVeggiePlate,
    label: "Jollof Rice + Chicken",
    tag: "Combo",
    price: 1850,
    span: "col-span-2 lg:col-span-2",
  },
  {
    src: ASSETS.afangSoup,
    label: "Afang Soup",
    tag: "Soups",
    price: 1000,
  },
  {
    src: ASSETS.suyaSkewers,
    label: "Suya",
    tag: "Grills",
    price: 1000,
  },
  {
    src: ASSETS.akaraCustard,
    label: "Pap + Akara",
    tag: "Breakfast",
    price: 1500,
  },
  {
    src: ASSETS.storefront,
    label: "Visit Meritz",
    tag: "Okpuno · Awka",
  },
];

export default function Gallery() {
  const { requestOrder } = useOrderCapture();
  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-20 sm:py-28 bg-[#FFFCF5] border-y border-[#E8E2D6]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 reveal">
          <div>
            <p className="text-[#D9534F] text-xs font-semibold tracking-[0.25em] uppercase">
              Inside Meritz
            </p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">
              Tap a plate, order it.
            </h2>
          </div>
          <a
            href={waLink("Hello Meritz! Saw your photos — want to order.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gallery-order-cta"
            className="inline-flex items-center gap-2 bg-[#2C2825] hover:bg-[#C73E3A] text-white font-semibold px-5 py-2.5 rounded-full transition-colors w-fit"
          >
            Order on WhatsApp
          </a>
        </div>

        <div
          data-testid="gallery-grid"
          className="mt-10 grid grid-cols-2 lg:grid-cols-4 auto-rows-[200px] sm:auto-rows-[240px] gap-3 sm:gap-4 reveal"
        >
          {SHOTS.map((s, i) => {
            const orderMsg = s.price
              ? `Hello Meritz! I'd like to order ${s.label} (₦${s.price.toLocaleString()}).`
              : `Hello Meritz! I'd like to visit — can you share opening hours?`;
            return (
              <button
                key={s.label}
                type="button"
                onClick={() => requestOrder(waLink(orderMsg))}
                data-testid={`gallery-tile-${i}`}
                className={`text-left relative group overflow-hidden rounded-3xl border border-[#E8E2D6] block ${s.span || ""}`}
              >
                <img
                  src={s.src}
                  alt={s.label}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                {s.price && (
                  <span
                    data-testid={`gallery-tile-${i}-price`}
                    className="absolute top-3 right-3 bg-[#F2B441] text-[#2C2825] font-bold text-xs sm:text-sm px-2.5 py-1 rounded-full shadow"
                  >
                    {formatNaira(s.price)}
                  </span>
                )}
                <figcaption className="absolute inset-x-0 bottom-0 p-3 sm:p-4">
                  <p className="text-[10px] sm:text-xs font-semibold tracking-[0.2em] uppercase text-[#F2B441]">
                    {s.tag}
                  </p>
                  <p className="text-white font-display text-base sm:text-lg font-extrabold leading-tight">
                    {s.label}
                  </p>
                </figcaption>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
